'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { Film, Tv, Laugh, Zap, Heart, Skull, Ghost, Drama, Sparkles, Swords, Building2, Crown, Rocket, Theater } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { tmdb } from '@/lib/tmdb';

interface Category {
  id: string;
  name: string;
  icon: any;
  gradient: string;
  genreIds: number[];
  type: 'movie' | 'tv' | 'both';
  backgroundImage?: string;
}

// TMDB Genre IDs:
// Movies: Action(28), Adventure(12), Animation(16), Comedy(35), Crime(80),
//         Documentary(99), Drama(18), Family(10751), Fantasy(14), History(36),
//         Horror(27), Music(10402), Mystery(9648), Romance(10749), Sci-Fi(878),
//         TV Movie(10770), Thriller(53), War(10752), Western(37)
// TV: Action & Adventure(10759), Animation(16), Comedy(35), Crime(80), Documentary(99),
//     Drama(18), Family(10751), Kids(10762), Mystery(9648), News(10763), Reality(10764),
//     Sci-Fi & Fantasy(10765), Soap(10766), Talk(10767), War & Politics(10768), Western(37)

const categories: Category[] = [
  {
    id: 'action',
    name: 'Action & Adventure',
    icon: Zap,
    gradient: 'from-orange-500 to-red-600',
    genreIds: [28, 12, 10759],
    type: 'both'
  },
  {
    id: 'comedy',
    name: 'Comedy',
    icon: Laugh,
    gradient: 'from-yellow-400 to-orange-500',
    genreIds: [35],
    type: 'both'
  },
  {
    id: 'drama',
    name: 'Drama',
    icon: Drama,
    gradient: 'from-purple-500 to-pink-600',
    genreIds: [18],
    type: 'both'
  },
  {
    id: 'horror',
    name: 'Horror',
    icon: Skull,
    gradient: 'from-gray-700 to-black',
    genreIds: [27],
    type: 'movie'
  },
  {
    id: 'thriller',
    name: 'Thriller & Mystery',
    icon: Ghost,
    gradient: 'from-indigo-600 to-purple-700',
    genreIds: [53, 9648, 80],
    type: 'both'
  },
  {
    id: 'romance',
    name: 'Romance',
    icon: Heart,
    gradient: 'from-pink-400 to-red-500',
    genreIds: [10749],
    type: 'movie'
  },
  {
    id: 'scifi',
    name: 'Sci-Fi & Fantasy',
    icon: Rocket,
    gradient: 'from-blue-500 to-cyan-400',
    genreIds: [878, 14, 10765],
    type: 'both'
  },
  {
    id: 'animation',
    name: 'Animation',
    icon: Sparkles,
    gradient: 'from-green-400 to-emerald-500',
    genreIds: [16],
    type: 'both'
  },
  {
    id: 'documentary',
    name: 'Documentary',
    icon: Building2,
    gradient: 'from-teal-500 to-blue-600',
    genreIds: [99],
    type: 'both'
  },
  {
    id: 'family',
    name: 'Family',
    icon: Crown,
    gradient: 'from-rose-400 to-pink-500',
    genreIds: [10751],
    type: 'both'
  },
  {
    id: 'war',
    name: 'War & History',
    icon: Swords,
    gradient: 'from-amber-700 to-red-800',
    genreIds: [10752, 36, 10768],
    type: 'both'
  },
  {
    id: 'western',
    name: 'Western',
    icon: Theater,
    gradient: 'from-yellow-700 to-amber-900',
    genreIds: [37],
    type: 'both'
  }
];

// Generate random delays for each category (0-2000ms range)
const categoryDelays: Record<string, number> = {};
categories.forEach(cat => {
  categoryDelays[cat.id] = Math.random() * 2000;
});

export function CategoryBrowser() {
  const router = useRouter();
  const [categoriesWithImages, setCategoriesWithImages] = useState<Category[]>(categories);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [visibleImages, setVisibleImages] = useState<Set<string>>(new Set());

  const handleImageLoad = useCallback((categoryId: string) => {
    setLoadedImages(prev => new Set(prev).add(categoryId));
    // Apply the random delay before showing
    setTimeout(() => {
      setVisibleImages(prev => new Set(prev).add(categoryId));
    }, categoryDelays[categoryId]);
  }, []);

  useEffect(() => {
    const fetchCategoryImages = async () => {
      try {
        const usedBackdrops = new Set<string>();
        const updatedCategories: Category[] = [];

        // Fetch sequentially to properly track used backdrops
        for (const category of categories) {
          try {
            // Get the first genre ID for movies
            const movieGenreId = category.genreIds.find(id => id < 100) || category.genreIds[0];

            // Fetch popular content for this genre
            const data = await tmdb.discoverMovies({
              with_genres: String(movieGenreId),
              sort_by: 'popularity.desc',
              page: 1
            });

            // Find a unique movie with a backdrop
            let movieWithBackdrop;
            for (const movie of (data.results || [])) {
              if (movie.backdrop_path && !usedBackdrops.has(movie.backdrop_path)) {
                movieWithBackdrop = movie;
                usedBackdrops.add(movie.backdrop_path);
                break;
              }
            }

            // If no unique backdrop found, try page 2 and 3
            if (!movieWithBackdrop) {
              for (let page = 2; page <= 3; page++) {
                const dataPage = await tmdb.discoverMovies({
                  with_genres: String(movieGenreId),
                  sort_by: 'popularity.desc',
                  page
                });

                for (const movie of (dataPage.results || [])) {
                  if (movie.backdrop_path && !usedBackdrops.has(movie.backdrop_path)) {
                    movieWithBackdrop = movie;
                    usedBackdrops.add(movie.backdrop_path);
                    break;
                  }
                }

                if (movieWithBackdrop) break;
              }
            }

            updatedCategories.push({
              ...category,
              backgroundImage: movieWithBackdrop?.backdrop_path
                ? tmdb.getImageUrl(movieWithBackdrop.backdrop_path, 'w500')
                : undefined
            });
          } catch (error) {
            console.error(`Error fetching image for ${category.name}:`, error);
            updatedCategories.push(category);
          }
        }

        setCategoriesWithImages(updatedCategories);
      } catch (error) {
        console.error('Error fetching category images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryImages();
  }, []);

  const handleCategoryClick = (category: Category) => {
    router.push(`/browse/${category.id}`);
  };

  return (
    <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
      <div className="flex items-center gap-2 px-4 md:px-0">
        <h2 className="flex items-center gap-2 text-xl md:text-2xl font-semibold tracking-tight text-white">
          <Film className="h-5 w-5 text-red-500 flex-shrink-0" />
          Browse by Category
        </h2>
      </div>

      {/* Mobile: Horizontal scroll | Desktop: Grid */}
      <div className="
        flex md:grid
        overflow-x-auto md:overflow-x-visible
        gap-3 md:gap-4
        px-4 md:px-0
        pb-2 md:pb-0
        scrollbar-hide
        snap-x snap-mandatory md:snap-none
        md:grid-cols-4 lg:grid-cols-6
      ">
        {categoriesWithImages.map((category) => {
          const Icon = category.icon;
          const isImageVisible = visibleImages.has(category.id);

          return (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              className="
                group relative overflow-hidden rounded-xl
                flex-shrink-0 md:flex-shrink
                w-28 md:w-auto
                aspect-[3/4]
                snap-start
                transition-all duration-300
                hover:scale-105 hover:shadow-2xl
              "
            >
              {/* Gradient placeholder - always visible initially */}
              <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-90 group-hover:opacity-100 transition-opacity`} />

              {/* Loading shimmer */}
              {loading && (
                <div className="absolute inset-0 bg-zinc-800/50 animate-pulse" />
              )}

              {/* Background Image with fade-in */}
              {category.backgroundImage && !loading && (
                <>
                  <Image
                    src={category.backgroundImage}
                    alt={category.name}
                    fill
                    sizes="(max-width: 768px) 112px, (max-width: 1024px) 25vw, 16vw"
                    className={`object-cover transition-opacity duration-500 ${isImageVisible ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() => handleImageLoad(category.id)}
                  />
                  {/* Dark overlay for readability - fade in with image */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40 transition-opacity duration-500 ${isImageVisible ? 'opacity-100' : 'opacity-0'}`} />
                  {/* Colored accent overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-30 group-hover:opacity-40 transition-opacity mix-blend-overlay`} />
                </>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />

              {/* Content */}
              <div className="relative h-full flex flex-col items-center justify-center p-3 md:p-4 text-white">
                <Icon className="h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 mb-1 md:mb-2 drop-shadow-2xl" />
                <span className="text-xs md:text-base lg:text-lg font-bold text-center drop-shadow-2xl leading-tight">
                  {category.name}
                </span>
              </div>

              {/* Shine Effect on Hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden md:block">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
